import type { ContactImportRow } from "@/lib/contactsDirectoryClient";
import {
  adminGhostBtnClass,
  insetSurfaceCardClass,
  secondaryBtnClass,
} from "@/components/common/surfaceClasses";
import type { ImportMatch, MatchResolution } from "./adminContactsDirectoryShared";
import { adminContactsModeTabClass } from "./adminContactsSurfaceClasses";
import { formatUsPhone } from "./adminContactsDirectoryUtils";

type ImportReviewProps = {
  importMatches: ImportMatch[];
  applyingImport: boolean;
  onResolutionChange: (key: string, resolution: MatchResolution) => void;
  onCopyAll: (key: string) => void;
  onCopyField: (key: string, field: keyof ContactImportRow) => void;
  onApply: () => void;
  onCancel: () => void;
};

export function AdminContactsImportReviewPanel(props: ImportReviewProps) {
  const { importMatches, applyingImport, onResolutionChange, onCopyAll, onCopyField, onApply, onCancel } = props;
  if (!importMatches.length) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 space-y-3 dark:border-amber-800/70 dark:bg-amber-950/30">
      <h3 className="font-semibold text-amber-900 dark:text-amber-200">Matched contacts review</h3>
      <div className="space-y-3">
        {importMatches.map((m) => (
          <div key={m.key} className={`${insetSurfaceCardClass} border-amber-200 p-3 space-y-2 dark:border-amber-900/50`}>
            <div className="text-xs text-gray-600 dark:text-zinc-400">Matched on: {m.matchReason}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-md border border-gray-200 p-2 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900/40">
                <div className="text-[11px] uppercase tracking-wide text-gray-600 dark:text-zinc-400 mb-1">Old contact</div>
                <div className="text-xs space-y-0.5 dark:text-zinc-300">
                  <div><strong>Name:</strong> {m.existing.name || "—"}</div>
                  <div><strong>Email:</strong> {m.existing.email || "—"}</div>
                  <div><strong>Phone:</strong> {formatUsPhone(m.existing.phone)}</div>
                  <div><strong>Company:</strong> {m.existing.company || "—"}</div>
                  <div><strong>Title:</strong> {m.existing.title || "—"}</div>
                </div>
              </div>
              <div className="rounded-md border border-indigo-200 p-2 bg-indigo-50/40 dark:border-indigo-800/60 dark:bg-indigo-950/30">
                <div className="text-[11px] uppercase tracking-wide text-indigo-700 dark:text-indigo-300 mb-1">New contact</div>
                <div className="text-xs space-y-0.5 dark:text-indigo-100">
                  <div><strong>Name:</strong> {m.incoming.name || "—"}</div>
                  <div><strong>Email:</strong> {m.incoming.email || "—"}</div>
                  <div><strong>Phone:</strong> {formatUsPhone(m.incoming.phone)}</div>
                  <div><strong>Company:</strong> {m.incoming.company || "—"}</div>
                  <div><strong>Title:</strong> {m.incoming.title || "—"}</div>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 text-xs dark:text-zinc-300">
              <label className="inline-flex items-center gap-1"><input type="radio" name={`resolution-${m.key}`} checked={m.resolution === "keep_old"} onChange={() => onResolutionChange(m.key, "keep_old")} />Keep old</label>
              <label className="inline-flex items-center gap-1"><input type="radio" name={`resolution-${m.key}`} checked={m.resolution === "keep_new"} onChange={() => onResolutionChange(m.key, "keep_new")} />Keep new</label>
              <label className="inline-flex items-center gap-1"><input type="radio" name={`resolution-${m.key}`} checked={m.resolution === "merge"} onChange={() => onResolutionChange(m.key, "merge")} />Merge fields</label>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button type="button" className="px-2 py-1 rounded border border-indigo-200 bg-indigo-50 text-xs text-indigo-800 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200" onClick={() => onCopyAll(m.key)}>Copy all non-empty fields</button>
              {(["name", "email", "phone", "company", "title", "department"] as const).map((field) => (
                <button key={field} type="button" className={`px-2 py-1 rounded ${secondaryBtnClass} text-xs`} onClick={() => onCopyField(m.key, field)}>Copy {field}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button type="button" disabled={applyingImport} onClick={onApply} className="px-3 py-1.5 rounded-lg bg-amber-700 text-white text-sm disabled:opacity-50">{applyingImport ? "Applying..." : "Apply import decisions"}</button>
        <button type="button" disabled={applyingImport} onClick={onCancel} className={`px-3 py-1.5 ${adminGhostBtnClass} border-amber-300 dark:border-amber-700`}>Cancel import</button>
      </div>
    </div>
  );
}

type TabsProps = {
  directoryMode: "contacts" | "general_support" | "add_contact" | "tags" | "org_tree";
  missingGeneralSupportFallback: boolean;
  onModeChange: (v: "contacts" | "general_support" | "add_contact" | "tags" | "org_tree") => void;
  onPrepareAddContact: () => void;
};

export function AdminContactsModeTabs(props: TabsProps) {
  const { directoryMode, missingGeneralSupportFallback, onModeChange, onPrepareAddContact } = props;
  const generalSupportInactiveExtra = missingGeneralSupportFallback
    ? "border-amber-400 bg-amber-50 text-amber-900 animate-admin-pending-pulse motion-reduce:animate-none dark:animate-admin-pending-pulse-dark ring-2 ring-amber-300/70 shadow-[0_0_14px_rgba(251,191,36,0.45)] hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/55"
    : "";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" onClick={() => onModeChange("contacts")} className={adminContactsModeTabClass(directoryMode === "contacts")}>Contacts directory</button>
      <button type="button" onClick={() => onModeChange("general_support")} className={adminContactsModeTabClass(directoryMode === "general_support", generalSupportInactiveExtra)}>General support contact</button>
      <button type="button" onClick={onPrepareAddContact} className={adminContactsModeTabClass(directoryMode === "add_contact")}>Add external contact</button>
      <button type="button" onClick={() => onModeChange("tags")} className={adminContactsModeTabClass(directoryMode === "tags")}>Path and topic tags</button>
      <button type="button" onClick={() => onModeChange("org_tree")} className={adminContactsModeTabClass(directoryMode === "org_tree")}>Org reporting tree</button>
    </div>
  );
}
